import React, {useEffect, useState, useCallback, useMemo} from 'react'
import {Typography, withStyles} from '@material-ui/core'
import {debounce} from 'lodash'
import FaultPercentageSlider from './FaultPercentageSlider'
import faultPercentageColors from './faultPercentageColors'

const styles = theme => ({
  questionText: {
    fontSize: 24,
    color: '#4F4F4F',
    margin: 8,
    width: '100%',
  },
  helpText: {
    fontSize: 16,
    color: '#4F4F4F',
    margin: 8,
  },
  totalSection: {
    textAlign: 'center',
    marginTop: 30,
    padding: '20px 0',
  },
  totalPercentage: {
    fontSize: 20,
    fontWeight: 700,
    color: '#4F4F4F',
    marginBottom: 8,
  },
  validationMessage: {
    fontSize: 16,
    color: '#EB5757',
    fontWeight: 500,
  },
  slidersContainer: {
    margin: '28px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    [theme.breakpoints.down('sm')]: {
      gap: 40,
    },
  },
})

const SurveyFaultPercentageAnswer = ({classes, question, setSelectedChoice, setIsValidAnswer}) => {
  const [values, setValues] = useState({})

  useEffect(() => {
    const initialValues = question.choices.reduce((acc, choice) => {
      acc[choice.toLowerCase()] = {
        label: choice,
        value: null,
      }
      return acc
    }, {})
    setValues(initialValues)
  }, [question])

  const debouncedUpdateData = useCallback(
    debounce((newValues, newTotal) => {
      const replacedNullValues = Object.fromEntries(
        Object.entries(newValues).map(([key, item]) => [
          key,
          {...item, value: !item.value ? 0 : item.value},
        ]),
      )
      const jsonString = JSON.stringify(replacedNullValues)
      setSelectedChoice(jsonString)
      setIsValidAnswer(newTotal === 100)
    }, 500),
    [setSelectedChoice, setIsValidAnswer],
  )

  const total = useMemo(
    () => Object.values(values).reduce((sum, item) => sum + (item.value || 0), 0),
    [values],
  )

  const handleValueChange = (party, newValue) => {
    setIsValidAnswer(false)
    setValues(prevValues => {
      const lowercaseParty = party.toLowerCase()
      return {
        ...prevValues,
        [lowercaseParty]: {
          ...prevValues[lowercaseParty],
          value: newValue,
        },
      }
    })
  }

  const handleValueChangeCommitted = (party, newValue) => {
    setValues(prevValues => {
      const lowercaseParty = party.toLowerCase()
      const newValues = {
        ...prevValues,
        [lowercaseParty]: {
          ...prevValues[lowercaseParty],
          value: newValue,
        },
      }
      const newTotal = Object.values(newValues).reduce((sum, item) => sum + (item.value || 0), 0)

      debouncedUpdateData(newValues, newTotal)

      return newValues
    })
  }

  return (
    <div style={{width: '100%'}}>
      <Typography className={classes.questionText}>{question.question}</Typography>
      <Typography className={classes.helpText}>
        Use the sliders or type numbers to assign fault, making sure the total adds up to 100%
      </Typography>

      <div className={classes.slidersContainer}>
        {question.choices.map((party, index) => (
          <FaultPercentageSlider
            key={`${party}-${index}`}
            party={party}
            value={values[party.toLowerCase()]?.value}
            onChange={value => handleValueChange(party, value)}
            onChangeCommitted={value => handleValueChangeCommitted(party, value)}
            color={faultPercentageColors[index]}
          />
        ))}
      </div>

      <div className={classes.totalSection}>
        <Typography className={classes.totalPercentage}>Total Percentage: {total}%</Typography>
        {total !== 100 && (
          <Typography className={classes.validationMessage}>Total must equal 100%</Typography>
        )}
      </div>
    </div>
  )
}

export default withStyles(styles)(SurveyFaultPercentageAnswer)
